import { DataMartModel } from "./model/types";
import { entityDataMartName } from "./entityDataMart";

/**
 * Which dimension a fact key column points at (`source_dimension_target_entity_dm_id`). The data
 * mart data quality tests check every value of the column against that dimension's key, so a key
 * that points nowhere is a key nobody checks.
 */

export type DimensionOption = {
  targetEntityDmId: number;
  name: string;
  schema: string | null;
  dataMart: string;
};

type KeyColumnLike = {
  target_entity_dm_column_name: string;
  source_dimension_target_entity_dm_id?: number | null;
  dimension_key?: string | boolean | null;
};

/**
 * Every saved dimension of the environment, once, grouped by data mart then name.
 * `dataMartNames` maps `data_mart_id` to the data mart's name (see `entityDataMartName`).
 */
export const buildDimensionOptions = (
  model: DataMartModel | null,
  dataMartNames: ReadonlyMap<number, string> = new Map()
): DimensionOption[] => {
  const byId = new Map<number, DimensionOption>();
  (model?.graphs ?? []).forEach((graph) => {
    graph.dimensions.forEach((dimension) => {
      const id = dimension.record.target_entity_dm_id;
      if (id == null || byId.has(id)) {
        return;
      }
      byId.set(id, {
        targetEntityDmId: id,
        name: dimension.record.target_entity_dm_name,
        schema: dimension.record.target_schema ?? null,
        dataMart: entityDataMartName(dimension.record, dataMartNames) || graph.dataMart.data_mart
      });
    });
  });
  return Array.from(byId.values()).sort(
    (a, b) => a.dataMart.localeCompare(b.dataMart) || a.name.localeCompare(b.name)
  );
};

const isFlagged = (value: string | boolean | null | undefined) =>
  value === true || (typeof value === "string" && value.trim() !== "" && value.toLowerCase() !== "false");

/** A key column (by flag or by its `_key` name) that points at no dimension. */
export const isUnlinkedKeyColumn = (column: KeyColumnLike, factName: string) => {
  const name = column.target_entity_dm_column_name?.trim() ?? "";
  if (!name || column.source_dimension_target_entity_dm_id != null) {
    return false;
  }
  if (name.toLowerCase() === `${factName}_key`.toLowerCase()) {
    return false;
  }
  return isFlagged(column.dimension_key) || name.toLowerCase().endsWith("_key");
};

/**
 * The dimension a key column is probably for, judged by name only — offered, never applied.
 *
 * `d_organization_key` → d_organization; role-playing keys carry the dimension's name at the
 * end or after it: `d_accounting_date_key` → d_date, `d_customer_paying_key` → d_customer. The
 * longest matching name wins; two dimensions that cannot be told apart suggest nothing.
 */
export const suggestDimension = (
  columnName: string,
  options: DimensionOption[]
): DimensionOption | null => {
  const lowered = columnName.trim().toLowerCase();
  if (!lowered.endsWith("_key")) {
    return null;
  }
  const stem = lowered.slice(0, -"_key".length);
  const matches = options.filter((option) => {
    const name = option.name.toLowerCase();
    const base = name.startsWith("d_") ? name.slice(2) : name;
    return stem === name || stem.startsWith(`${name}_`) || stem.endsWith(`_${base}`);
  });
  if (!matches.length) {
    return null;
  }
  const longest = Math.max(...matches.map((option) => option.name.length));
  const best = matches.filter((option) => option.name.length === longest);
  return best.length === 1 ? best[0] : null;
};
